import blinkscribeapi from "../../api/blinkscribeapi";
import {
  combineIntoApp,
  combineTargetOf,
  shouldAskCombine,
  shouldAskSigned,
  visitChoices,
} from "../../utils/otherUserNote";
import { requestCombineChoice, requestSignedNoteChoice } from "./VisitConsentHost";

/* Before a chart recording (after the visit gate), one read-only lookup
   (GET /v1/recording/appointment/{id}/combine-target) and at most one question:

   1. The visit already has ANOTHER user's SIGNED (or posted) note and none of
      this user's (10-06, patient 4662: provider A's signed note on a card
      reassigned to provider B; B's recording there would post into A's
      encounter). Offer the user's own visit today instead; only when there is
      none, a separate note here with a warning; or Cancel.
   2. Otherwise, the visit has another user's unsigned note and none of this
      user's (incident 10-06, recording 48693): add to that note (the explicit
      combine BlinkScribe still honours), keep a separate note, or Cancel.

   Returns { combineInto, answered, cancelled, switchTo }:
     combineInto - { id, audiolink } for startRecording's combine, or null
     answered    - the user saw a question (the server's own warning about
                   the same note is then not shown again)
     cancelled   - the user cancelled: do not start
     switchTo    - the visit (patient_visits_today row) to record on instead:
                   the caller re-points the chart to it and checks again
   allowSwitch=false (the check after a switch): no visit is offered again.
   Any lookup problem (old server, network, no appointment): nothing asked,
   BlinkScribe decides - a separate note plus its warning. */
const NONE = { combineInto: null, answered: false, cancelled: false, switchTo: null };

export const chooseCombineTarget = async (appointmentid, { timeout = 8000, allowSwitch = true } = {}) => {
  const appt = String(appointmentid ?? "").trim();
  if (!/^\d+$/.test(appt) || Number(appt) <= 0) return NONE;
  let target = null;
  try {
    const body = await blinkscribeapi.get(`/v1/recording/appointment/${appt}/combine-target`, { timeout });
    target = combineTargetOf(body);
  } catch (_) {
    target = null;
  }
  if (shouldAskSigned(target)) {
    const choices = allowSwitch ? visitChoices(target) : { kind: "none", visits: [] };
    const choice = await requestSignedNoteChoice({ signed: target.signed, choices });
    if (choice && choice.action === "switch" && choice.visit) {
      return { ...NONE, answered: true, switchTo: choice.visit };
    }
    if (choice && choice.action === "separate") return { ...NONE, answered: true };
    return { ...NONE, answered: true, cancelled: true };
  }
  if (!shouldAskCombine(target)) return NONE;
  const choice = await requestCombineChoice(target.other);
  if (choice === "add") {
    const into = combineIntoApp(target.other);
    return into ? { ...NONE, combineInto: into, answered: true } : NONE;
  }
  if (choice === "separate") return { ...NONE, answered: true };
  return { ...NONE, answered: true, cancelled: true };
};
